import { monthKeysBack, startOfToday } from './dates';
import type {
  Category,
  CategoryId,
  Person,
  PersonId,
  Site,
  SiteId,
} from './types';

export interface BusinessSegment {
  id: 'electrical' | 'industrial-automation' | 'value-added-services';
  name: string;
  /** FY2025 revenue, USD. */
  revenueFy2025: number;
  /** Product families and services the segment covers. */
  offerings: string[];
}

export interface Company {
  name: string;
  legalName: string;
  tagline: string;
  fiscalYear: string;
  /** FY2025 revenue, USD. */
  revenueFy2025: number;
  employees: number;
  distributionCenters: number;
  branches: number;
  /** States with at least one branch. */
  branchStates: string[];
  segments: BusinessSegment[];
}

export const COMPANY: Company = {
  name: 'Pillar Industrial Supply',
  legalName: 'Pillar Industrial Supply, Inc.',
  tagline:
    'Industrial and electrical distribution with a UL-certified control panel shop and cut-to-length wire',
  fiscalYear: 'FY2025',
  /** FY2025 revenue, USD. */
  revenueFy2025: 4_600_000_000,
  employees: 3_400,
  distributionCenters: 3,
  branches: 41,
  branchStates: ['TX', 'AZ', 'NM', 'NV', 'CA'],
  segments: [
    {
      id: 'electrical',
      name: 'Electrical',
      revenueFy2025: 2_820_000_000,
      offerings: [
        'Wire & cable',
        'Lighting',
        'Circuit protection',
        'Wiring devices',
        'Conduit & wire routing',
        'Pillar Pro private label',
      ],
    },
    {
      id: 'industrial-automation',
      name: 'Industrial automation and maintenance supplies',
      revenueFy2025: 1_260_000_000,
      offerings: ['Drives & automation', 'Enclosures', 'Maintenance supplies'],
    },
    {
      id: 'value-added-services',
      name: 'Value-added services',
      revenueFy2025: 520_000_000,
      offerings: ['Control panel shop (UL-certified)', 'Cut-to-length wire'],
    },
  ],
};

/** The demo persona: Sarah Chen opens the procurement overview each morning. */
export const PERSONA_ID: PersonId = 'sarah-chen';

export const SITES: Record<SiteId, Site> = {
  DAL: {
    id: 'DAL',
    name: 'Dallas DC',
    city: 'Dallas',
    region: 'TX',
    country: 'United States',
    kind: 'dc',
    role: 'Main distribution center next to headquarters; also runs the cut-to-length wire service',
    brands: ['Pillar Pro'],
  },
  PHX: {
    id: 'PHX',
    name: 'Phoenix DC',
    city: 'Phoenix',
    region: 'AZ',
    country: 'United States',
    kind: 'dc',
    role: 'Regional distribution center replenishing the Arizona, New Mexico and Nevada branches',
    brands: ['Pillar Pro'],
  },
  HAY: {
    id: 'HAY',
    name: 'Hayward DC',
    city: 'Hayward',
    region: 'CA',
    country: 'United States',
    kind: 'dc',
    role: 'West coast distribution center (CA-01) serving the California and Nevada branches',
    brands: ['Pillar Pro'],
  },
  PNL: {
    id: 'PNL',
    name: 'Dallas panel shop',
    city: 'Dallas',
    region: 'TX',
    country: 'United States',
    kind: 'plant',
    role: 'UL-certified control panel shop building control panels, power distribution panels and motor drive retrofit kits',
    brands: [],
  },
  HQ: {
    id: 'HQ',
    name: 'Dallas headquarters',
    city: 'Dallas',
    region: 'TX',
    country: 'United States',
    kind: 'office',
    role: 'Corporate office: finance, IT, legal, marketing and indirect procurement',
    brands: [],
  },
};

export const SITE_LIST: Site[] = Object.values(SITES);

export function getSite(id: SiteId): Site {
  return SITES[id];
}

export const PEOPLE: Record<PersonId, Person> = {
  'sarah-chen': {
    id: 'sarah-chen',
    name: 'Sarah Chen',
    title: 'Category Manager, Electrical',
    initials: 'SC',
    siteId: 'HQ',
  },
  'daniel-reyes': {
    id: 'daniel-reyes',
    name: 'Daniel Reyes',
    title: 'Director, Indirect Procurement',
    initials: 'DR',
    siteId: 'HQ',
  },
  'priya-raman': {
    id: 'priya-raman',
    name: 'Priya Raman',
    title: 'Supplier Quality Manager',
    initials: 'PR',
    siteId: 'DAL',
  },
  'tom-gallagher': {
    id: 'tom-gallagher',
    name: 'Tom Gallagher',
    title: 'AP Manager',
    initials: 'TG',
    siteId: 'HQ',
  },
  'lauren-brooks': {
    id: 'lauren-brooks',
    name: 'Lauren Brooks',
    title: 'Marketing Director, Pillar Pro',
    initials: 'LB',
    siteId: 'HQ',
  },
  'kevin-oneill': {
    id: 'kevin-oneill',
    name: "Kevin O'Neill",
    title: 'IT Director',
    initials: 'KO',
    siteId: 'HQ',
  },
  'sam-whitfield': {
    id: 'sam-whitfield',
    name: 'Sam Whitfield',
    title: 'VP Finance',
    initials: 'SW',
    siteId: 'HQ',
  },
  'elena-novak': {
    id: 'elena-novak',
    name: 'Elena Novak',
    title: 'Senior Counsel, Commercial',
    initials: 'EN',
    siteId: 'HQ',
  },
  'marcus-hale': {
    id: 'marcus-hale',
    name: 'Marcus Hale',
    title: 'VP Supply Chain',
    initials: 'MH',
    siteId: 'HQ',
  },
};

export const PEOPLE_LIST: Person[] = Object.values(PEOPLE);

export function getPerson(id: PersonId): Person {
  return PEOPLE[id];
}

/**
 * Spend categories. Direct categories sum to $2.11B and indirect to $1.73B
 * (the TTM addressable spend of $3.84B, 55% direct and 45% indirect); contract
 * coverage is spend-weighted to 86% direct, 41% indirect and 66% overall.
 * Wire & cable is the largest direct category, as it is for most electrical
 * distributors. Indirect categories are the original mix scaled up so spend is
 * close to an even split. Maintenance & industrial supplies is the long-tail
 * category the Replenishment demo buys from (MRO Supply Co.).
 */
export const CATEGORIES: Record<CategoryId, Category> = {
  'cat-wire': {
    id: 'cat-wire',
    name: 'Wire & cable',
    type: 'direct',
    spendTtm: 490_000_000,
    supplierCount: 195,
    contractCoverage: 92,
    ownerId: 'sarah-chen',
  },
  'cat-circuit': {
    id: 'cat-circuit',
    name: 'Circuit protection',
    type: 'direct',
    spendTtm: 285_000_000,
    supplierCount: 130,
    contractCoverage: 90,
    ownerId: 'sarah-chen',
  },
  'cat-lighting': {
    id: 'cat-lighting',
    name: 'Lighting',
    type: 'direct',
    spendTtm: 315_000_000,
    supplierCount: 150,
    contractCoverage: 84,
    ownerId: 'sarah-chen',
  },
  'cat-conduit': {
    id: 'cat-conduit',
    name: 'Conduit & wire routing',
    type: 'direct',
    spendTtm: 180_000_000,
    supplierCount: 110,
    contractCoverage: 88,
    ownerId: 'sarah-chen',
  },
  'cat-drives': {
    id: 'cat-drives',
    name: 'Drives & automation',
    type: 'direct',
    spendTtm: 270_000_000,
    supplierCount: 140,
    contractCoverage: 82,
    ownerId: 'sarah-chen',
  },
  'cat-enclosures': {
    id: 'cat-enclosures',
    name: 'Enclosures & panel components',
    type: 'direct',
    spendTtm: 150_000_000,
    supplierCount: 120,
    contractCoverage: 85,
    ownerId: 'marcus-hale',
  },
  'cat-power': {
    id: 'cat-power',
    name: 'Transformers & power distribution',
    type: 'direct',
    spendTtm: 190_000_000,
    supplierCount: 85,
    contractCoverage: 80,
    ownerId: 'marcus-hale',
  },
  'cat-devices': {
    id: 'cat-devices',
    name: 'Wiring devices',
    type: 'direct',
    spendTtm: 120_000_000,
    supplierCount: 110,
    contractCoverage: 75,
    ownerId: 'sarah-chen',
  },
  'cat-mro': {
    id: 'cat-mro',
    name: 'Maintenance & industrial supplies',
    type: 'direct',
    spendTtm: 110_000_000,
    supplierCount: 80,
    contractCoverage: 80,
    ownerId: 'sarah-chen',
  },
  'cat-it-software': {
    id: 'cat-it-software',
    name: 'IT & software',
    type: 'indirect',
    spendTtm: 299_000_000,
    supplierCount: 430,
    contractCoverage: 60,
    ownerId: 'daniel-reyes',
  },
  'cat-it-hardware': {
    id: 'cat-it-hardware',
    name: 'IT hardware',
    type: 'indirect',
    spendTtm: 145_000_000,
    supplierCount: 190,
    contractCoverage: 52,
    ownerId: 'daniel-reyes',
  },
  'cat-prof-services': {
    id: 'cat-prof-services',
    name: 'Professional services',
    type: 'indirect',
    spendTtm: 252_000_000,
    supplierCount: 620,
    contractCoverage: 36,
    ownerId: 'daniel-reyes',
  },
  'cat-marketing': {
    id: 'cat-marketing',
    name: 'Marketing & media',
    type: 'indirect',
    spendTtm: 215_000_000,
    supplierCount: 380,
    contractCoverage: 34,
    ownerId: 'daniel-reyes',
  },
  'cat-logistics': {
    id: 'cat-logistics',
    name: 'Logistics & freight',
    type: 'indirect',
    spendTtm: 524_000_000,
    supplierCount: 360,
    contractCoverage: 40,
    ownerId: 'daniel-reyes',
  },
  'cat-contingent': {
    id: 'cat-contingent',
    name: 'Contingent labor',
    type: 'indirect',
    spendTtm: 178_000_000,
    supplierCount: 160,
    contractCoverage: 42,
    ownerId: 'daniel-reyes',
  },
  'cat-facilities': {
    id: 'cat-facilities',
    name: 'Facilities & office',
    type: 'indirect',
    spendTtm: 117_000_000,
    supplierCount: 680,
    contractCoverage: 11,
    ownerId: 'daniel-reyes',
  },
};

export const CATEGORY_LIST: Category[] = Object.values(CATEGORIES);
export const DIRECT_CATEGORIES: Category[] = CATEGORY_LIST.filter(
  (c) => c.type === 'direct',
);
export const INDIRECT_CATEGORIES: Category[] = CATEGORY_LIST.filter(
  (c) => c.type === 'indirect',
);

export function getCategory(id: CategoryId): Category {
  return CATEGORIES[id];
}

/** Canonical company-level numbers. Trailing 12 months unless noted. */
export const SPEND_KPIS = {
  addressableSpend: 3_840_000_000,
  directSpend: 2_110_000_000,
  indirectSpend: 1_730_000_000,
  directSharePct: 55,
  indirectSharePct: 45,

  spendUnderManagementPct: 76,
  contractCoveragePct: 66,
  contractCoverageDirectPct: 86,
  contractCoverageIndirectPct: 41,

  activeSuppliers: 3_940,
  directSuppliers: 1_120,
  indirectSuppliers: 2_820,
  /** Strategic and preferred suppliers shown in the demo tables. */
  curatedSupplierCount: 26,

  savingsNegotiatedYtd: 31_200_000,
  savingsRealizedYtd: 24_600_000,

  invoicesCheckedYtd: 21_906,
  linesFlaggedYtd: 1_284,
  leakagePreventedYtd: 4_370_000,
  workingCapitalReleasedYtd: 3_600_000,
  earlyPayDiscountsCapturedYtd: 412_000,

  /** Direct operations: on-time in-full percent and incoming PPM. */
  otifPct: 93.4,
  incomingPpm: 310,

  openRequests: 47,
  intakeToPoDays: 4.8,
  intakeToPoDaysBefore: 21,

  criticalAlerts: 3,
  highAlerts: 7,
  /** Spend exposed to new tariffs, USD, and its share of addressable spend. */
  tariffExposedSpend: 186_000_000,
  tariffExposedPct: 4.8,

  activeAgreements: 1_946,
  clausesExtracted: 41_580,
  /** Renewals or notice deadlines falling in the next 90 days. */
  renewalsNext90Days: 38,
};

export interface MonthlySpendPoint {
  /** Month label from `monthKeysBack`, e.g. "Oct 2026". */
  month: string;
  direct: number;
  indirect: number;
}

// Calendar-month weights, January first. Normalized below so a year sums to 12.
// Distributor buying follows the construction season, peaking April to September.
const DIRECT_SEASONALITY = [
  0.9, 0.92, 0.99, 1.05, 1.08, 1.1, 1.07, 1.09, 1.07, 1.03, 0.94, 0.86,
];
const INDIRECT_SEASONALITY = [
  0.93, 0.95, 1.0, 1.04, 1.05, 1.06, 1.03, 1.04, 1.03, 1.0, 0.95, 0.92,
];

function seasonalSeries(
  annualTotal: number,
  seasonality: number[],
  yoyGrowthPct: number,
): number[] {
  const currentMonth = startOfToday().getMonth();
  const weightSum = seasonality.reduce((sum, w) => sum + w, 0);
  const priorAnnual = annualTotal / (1 + yoyGrowthPct / 100);
  const values: number[] = [];
  for (let i = 0; i < 24; i += 1) {
    const calendarMonth = (((currentMonth - (23 - i)) % 12) + 12) % 12;
    const weight = seasonality[calendarMonth] / weightSum;
    const annual = i < 12 ? priorAnnual : annualTotal;
    // Small deterministic wobble so the prior year is not a scaled copy.
    const wobble = 1 + 0.012 * Math.sin(i * 1.7 + annualTotal / 1e8);
    // Round to $10K.
    values.push(Math.round((annual * weight * wobble) / 10_000) * 10_000);
  }
  // Put the rounding residual on the largest of the last 12 months so the
  // trailing-12-month total is exactly the annual figure.
  const lastTwelve = values.slice(12);
  const residual = annualTotal - lastTwelve.reduce((sum, v) => sum + v, 0);
  const peak = lastTwelve.indexOf(Math.max(...lastTwelve));
  values[12 + peak] += residual;
  return values;
}

const DIRECT_MONTHLY = seasonalSeries(
  SPEND_KPIS.directSpend,
  DIRECT_SEASONALITY,
  5.8,
);
const INDIRECT_MONTHLY = seasonalSeries(
  SPEND_KPIS.indirectSpend,
  INDIRECT_SEASONALITY,
  4.1,
);

/** 24 months, oldest first. The last 12 sum to the TTM direct and indirect spend. */
export const MONTHLY_SPEND: MonthlySpendPoint[] = monthKeysBack(24).map(
  (month, i) => ({
    month,
    direct: DIRECT_MONTHLY[i],
    indirect: INDIRECT_MONTHLY[i],
  }),
);

export interface SavingsQuarter {
  quarter: string;
  /** USD negotiated (contracted) savings in the quarter. */
  negotiated: number;
  /** USD savings realized on invoices and receipts in the quarter. */
  realized: number;
  /** True for the in-progress quarter. */
  partial?: boolean;
}

/** FY2026 by quarter. Sums to $31.2M negotiated and $24.6M realized YTD. */
export const SAVINGS_BY_QUARTER: SavingsQuarter[] = [
  { quarter: 'Q1 2026', negotiated: 6_800_000, realized: 5_200_000 },
  { quarter: 'Q2 2026', negotiated: 9_600_000, realized: 7_100_000 },
  { quarter: 'Q3 2026', negotiated: 11_900_000, realized: 9_700_000 },
  {
    quarter: 'Q4 2026',
    negotiated: 2_900_000,
    realized: 2_600_000,
    partial: true,
  },
];
